/**
 * Tax Intelligence Engine — Tax Center (/tax-center).
 *
 * Page unique à sections (Synthèse / Opportunités / Calendrier /
 * Justificatifs / Simulations / Détails) sur la trame visuelle sombre de
 * CompteResultat. Les cartes SmartFeed pointent vers
 * /tax-center?section=calendar|opportunities|documents|details : on lit
 * `?section=` (trame « filtre dans le lien ») pour ouvrir la bonne section.
 *
 * Principes respectés côté UI :
 * - honnêteté : bandeau « estimation indicative » si result.reliable=false,
 *   écran de couverture si le pays n'est pas couvert (jamais de chiffres
 *   inventés) ;
 * - progressive profiling : les questions en attente s'affichent UNE par
 *   une en tête de page, réponse ou passe — jamais un formulaire complet ;
 * - le copilot explique, il ne calcule pas (POST /api/tax/copilot/query,
 *   timeout étendu car appel LLM côté backend).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  makeDeleteRequest,
  makeGetAllRequest,
  makeGetRequest,
  makePatchRequest,
  makePostRequest,
} from 'utils/api'
import urls from 'utils/urls'

// ---------------------------------------------------------------- palette
// Même DA sombre que CompteResultat (LAP-768) pour rester cohérent.
const P = {
  bg: 'linear-gradient(180deg, #0a1024 0%, #0e1a36 100%)',
  card: 'rgba(255,255,255,0.035)',
  border: 'rgba(255,255,255,0.10)',
  text: '#e8eefc',
  textDim: '#93a4c8',
  accent: '#7e9cdb',
  positive: '#4ade80',
  negative: '#f87171',
  warn: '#fbbf24',
  mono: '"Roboto Mono","SFMono-Regular","Courier New",monospace',
}

const SECTIONS = [
  'overview',
  'opportunities',
  'calendar',
  'documents',
  'operations',
  'simulations',
  'details',
]

// Types de faits proposés pour le flag manuel opération → fait fiscal
// (sous-ensemble « dépenses / paiements » de C.FACT_TYPES côté backend).
// 'EXCLUDED' retire l'opération de la détection auto (faux positifs, ex.
// allocations détectées comme déduction).
const TAGGABLE_FACT_TYPES = [
  'EMPLOYMENT_INCOME',
  'RETIREMENT_CONTRIBUTION',
  'PENSION_BUYBACK',
  'CHARITABLE_DONATION',
  'CHILDCARE_EXPENSE',
  'ALIMONY_PAID',
  'PROFESSIONAL_EXPENSE',
  'PROFESSIONAL_TRAINING',
  'COMMUTE_EXPENSE',
  'HEALTH_EXPENSE',
  'INSURANCE_PREMIUM',
  'PROPERTY_MAINTENANCE_EXPENSE',
  'MORTGAGE_INTEREST',
  'WITHHOLDING_TAX',
  'TAX_INSTALLMENT_PAID',
]

const fmtMoney = (n, currency = 'CHF') =>
  n === null || n === undefined
    ? '—'
    : `${Number(n).toLocaleString('fr-CH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency}`

const fmtDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-CH')
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: P.text,
    fontFamily: P.mono,
    fontSize: 13,
    '& fieldset': { borderColor: P.border },
    '&:hover fieldset': { borderColor: P.accent },
  },
  '& .MuiInputLabel-root': { color: P.textDim, fontSize: 13 },
}
const selectSx = {
  color: P.text,
  fontFamily: P.mono,
  fontSize: 13,
  '.MuiOutlinedInput-notchedOutline': { borderColor: P.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: P.accent },
  '.MuiSvgIcon-root': { color: P.textDim },
}
const menuProps = { PaperProps: { sx: { bgcolor: '#0e1a36', color: P.text } } }

function Card({ title, accent, right, children, sx }) {
  return (
    <Box
      sx={{
        bgcolor: P.card,
        border: `1px solid ${P.border}`,
        borderRadius: 2,
        p: { xs: 1.5, sm: 2 },
        mb: 1.5,
        ...sx,
      }}
    >
      {title && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: accent || P.accent,
              }}
            />
            <Typography
              sx={{
                color: P.text,
                fontSize: 12.5,
                letterSpacing: 0.8,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}
            >
              {title}
            </Typography>
          </Stack>
          {right}
        </Stack>
      )}
      {children}
    </Box>
  )
}

function Kpi({ label, value, sub, color }) {
  return (
    <Box
      sx={{
        bgcolor: P.card,
        border: `1px solid ${P.border}`,
        borderRadius: 2,
        p: 1.5,
        minWidth: 0,
        flex: '1 1 150px',
      }}
    >
      <Typography sx={{ color: P.textDim, fontSize: 11.5, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          color: color || P.text,
          fontFamily: P.mono,
          fontSize: 17,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ color: P.textDim, fontSize: 11 }}>{sub}</Typography>
      )}
    </Box>
  )
}

function StatusChip({ label, tone }) {
  const colors = {
    good: { c: P.positive, bg: 'rgba(74,222,128,0.12)' },
    warn: { c: P.warn, bg: 'rgba(251,191,36,0.12)' },
    bad: { c: P.negative, bg: 'rgba(248,113,113,0.12)' },
    dim: { c: P.textDim, bg: 'rgba(255,255,255,0.06)' },
  }
  const { c, bg } = colors[tone] || colors.dim
  return (
    <Chip
      label={label}
      size="small"
      sx={{ color: c, bgcolor: bg, fontSize: 11, fontWeight: 600 }}
    />
  )
}

// ------------------------------------------- progressive profiling (1 à 1)
function QuestionPanel({ question, onAnswered, t }) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async (payload) => {
    setBusy(true)
    const res = await makePostRequest(
      urls.taxAnswerQuestion(question.id),
      payload,
      {},
      true,
    )
    setBusy(false)
    if (res?.status === 200) onAnswered()
  }

  const type = question.question_type
  return (
    <Card title={t('taxCenter.questionsTitle')} accent={P.warn}>
      <Typography sx={{ color: P.text, fontSize: 14, mb: 0.5 }}>
        {question.question_text}
      </Typography>
      <Typography sx={{ color: P.textDim, fontSize: 11.5, mb: 1.5 }}>
        {t('taxCenter.questionsHint')}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {type === 'YES_NO' && (
          <>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => submit({ value: true })}
              sx={{ color: P.positive, borderColor: P.border }}
            >
              {t('taxCenter.yes')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={() => submit({ value: false })}
              sx={{ color: P.negative, borderColor: P.border }}
            >
              {t('taxCenter.no')}
            </Button>
          </>
        )}
        {type === 'CHOICE' && (
          <Select
            size="small"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            displayEmpty
            sx={selectSx}
            MenuProps={menuProps}
          >
            {(question.choices || []).map((c) => (
              <MenuItem key={String(c)} value={c}>
                {String(c)}
              </MenuItem>
            ))}
          </Select>
        )}
        {(type === 'AMOUNT' || type === 'PERCENTAGE' || type === 'TEXT') && (
          <TextField
            size="small"
            type={type === 'TEXT' ? 'text' : 'number'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={fieldSx}
          />
        )}
        {type !== 'YES_NO' && (
          <Button
            size="small"
            variant="contained"
            disabled={busy || value === ''}
            onClick={() =>
              submit({
                value: type === 'TEXT' ? value : Number(value),
                ...(type === 'PERCENTAGE' ? { unit: '%' } : {}),
              })
            }
          >
            {t('taxCenter.answer')}
          </Button>
        )}
        <Button
          size="small"
          disabled={busy}
          onClick={() => submit({ skip: true })}
          sx={{ color: P.textDim }}
        >
          {t('taxCenter.skip')}
        </Button>
        {busy && <CircularProgress size={16} sx={{ color: P.accent }} />}
      </Stack>
    </Card>
  )
}

// ------------------------------------------------------------------ copilot
function CopilotBox({ t }) {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const ask = async () => {
    if (!question.trim()) return
    setBusy(true)
    setError('')
    // Timeout étendu : le backend appelle un LLM (cf. utils/api.js — les
    // routes IA dépassent couramment les 20 s par défaut).
    const res = await makePostRequest(
      urls.taxCopilotQuery,
      { question },
      { custom: { timeout: 180000 } },
      true,
    )
    setBusy(false)
    if (res?.status === 200) setAnswer(res.data.answer || '')
    else setError(res?.data?.message || res?.data?.detail || 'Erreur')
  }

  return (
    <Card title={t('taxCenter.copilotTitle')} accent={P.accent}>
      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('taxCenter.copilotPlaceholder')}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !busy && ask()}
          sx={fieldSx}
        />
        <Button variant="contained" disabled={busy} onClick={ask}>
          {busy ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            t('taxCenter.copilotAsk')
          )}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
      {answer && (
        <Typography
          sx={{ color: P.text, fontSize: 13.5, mt: 1.5, whiteSpace: 'pre-wrap' }}
        >
          {answer}
        </Typography>
      )}
      <Typography sx={{ color: P.textDim, fontSize: 11, mt: 1 }}>
        {t('taxCenter.copilotDisclaimer')}
      </Typography>
    </Card>
  )
}

// ------------------------------------------------------------ simulations
const SCENARIO_TYPES = [
  'RETIREMENT_CONTRIBUTION',
  'SALARY_CHANGE',
  'BONUS',
  'MOVE_MUNICIPALITY',
  'PROPERTY_PURCHASE',
  'MARRIAGE',
  'CHILD',
  'INVESTMENT_SALE',
  'CUSTOM',
]

function SimulationForm({ onCreated, t }) {
  const [name, setName] = useState('')
  const [type, setType] = useState('RETIREMENT_CONTRIBUTION')
  const [factType, setFactType] = useState('RETIREMENT_CONTRIBUTION')
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const run = async () => {
    setBusy(true)
    setError('')
    const res = await makePostRequest(
      urls.taxScenarios,
      {
        name: name || type,
        scenario_type: type,
        overridden_facts: [
          { fact_type: factType.trim().toUpperCase(), value: Number(value), mode: 'set' },
        ],
      },
      {},
      true,
    )
    setBusy(false)
    if (res?.status === 201) {
      setName('')
      setValue('')
      onCreated()
    } else {
      setError(JSON.stringify(res?.data) || 'Erreur')
    }
  }

  return (
    <Card title={t('taxCenter.simCreate')} accent={P.accent}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
        <TextField
          size="small"
          label={t('taxCenter.simName')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ ...fieldSx, minWidth: 180 }}
        />
        <Select
          size="small"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={selectSx}
          MenuProps={menuProps}
        >
          {SCENARIO_TYPES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          label={t('taxCenter.simFactType')}
          value={factType}
          onChange={(e) => setFactType(e.target.value)}
          sx={{ ...fieldSx, minWidth: 240 }}
        />
        <TextField
          size="small"
          type="number"
          label={t('taxCenter.simValue')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{ ...fieldSx, minWidth: 130 }}
        />
        <Button
          variant="contained"
          disabled={busy || value === '' || !factType.trim()}
          onClick={run}
        >
          {busy ? t('taxCenter.simRunning') : t('taxCenter.simRun')}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Card>
  )
}

// ============================================================== page
export function TaxCenterPage() {
  const { t } = useTranslation('layout')
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionParam = (searchParams.get('section') || '').toLowerCase()
  const section = SECTIONS.includes(sectionParam) ? sectionParam : 'overview'

  const [loading, setLoading] = useState(true)
  const [recalc, setRecalc] = useState(false)
  const [position, setPosition] = useState(null)
  const [questions, setQuestions] = useState([])
  const [opportunities, setOpportunities] = useState([])
  const [events, setEvents] = useState([])
  const [documents, setDocuments] = useState([])
  const [scenarios, setScenarios] = useState([])
  const [rules, setRules] = useState([])
  const [coverage, setCoverage] = useState(null)
  const [operations, setOperations] = useState([])
  const [opsQuery, setOpsQuery] = useState('')
  const [opsLoading, setOpsLoading] = useState(false)
  const [snack, setSnack] = useState('')

  const currency = position?.currency || 'CHF'

  const loadPosition = useCallback(async () => {
    // GET /position déclenche ensure_fresh côté backend (recalcul lazy si
    // le profil est dirty) : c'est LA porte d'entrée de la page.
    const [pos, missing] = await Promise.all([
      makeGetRequest(urls.taxPosition, {}, true),
      makeGetRequest(urls.taxMissingInformation, {}, true),
    ])
    if (pos?.status === 200) setPosition(pos.data)
    if (missing?.status === 200) setQuestions(missing.data.questions || [])
    return pos?.status === 200
  }, [])

  const loadSectionData = useCallback(async () => {
    // Collections paginées (PAGE_SIZE=20) → makeGetAllRequest, jamais
    // data.results direct (piège récurrent).
    const [opp, cal, doc, sce, rul, cov] = await Promise.all([
      makeGetAllRequest(urls.taxOpportunities, {}, true),
      makeGetAllRequest(urls.taxCalendar, {}, true),
      makeGetAllRequest(urls.taxDocuments, {}, true),
      makeGetAllRequest(urls.taxScenarios, {}, true),
      makeGetRequest(urls.taxRules, {}, true), // liste nue, non paginée
      makeGetRequest(urls.taxCoverage, {}, true),
    ])
    if (opp?.status === 200) setOpportunities(opp.data.results || [])
    if (cal?.status === 200) setEvents(cal.data.results || [])
    if (doc?.status === 200) setDocuments(doc.data.results || [])
    if (sce?.status === 200) setScenarios(sce.data.results || [])
    if (rul?.status === 200) setRules(Array.isArray(rul.data) ? rul.data : [])
    if (cov?.status === 200) setCoverage(cov.data)
  }, [])

  const loadOperations = useCallback(async (q = '') => {
    // Liste NON paginée (payload dédié {operations: [...]}, cap backend 400).
    setOpsLoading(true)
    const url = q
      ? `${urls.taxOperations}?q=${encodeURIComponent(q)}`
      : urls.taxOperations
    const res = await makeGetRequest(url, {}, true)
    if (res?.status === 200) setOperations(res.data.operations || [])
    setOpsLoading(false)
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    await loadPosition()
    await loadSectionData()
    setLoading(false)
  }, [loadPosition, loadSectionData])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    // Chargement paresseux : la liste des opérations n'est demandée qu'à
    // l'ouverture de la section (la recherche relance loadOperations).
    if (section === 'operations') loadOperations()
  }, [section, loadOperations])

  const setSection = (value) => {
    const next = new URLSearchParams(searchParams)
    if (value === 'overview') next.delete('section')
    else next.set('section', value)
    setSearchParams(next, { replace: true })
  }

  const recalculate = async () => {
    setRecalc(true)
    const res = await makePostRequest(urls.taxRecalculate, {}, {}, true)
    if (res?.status === 200) {
      // Le POST renvoie le dashboard sans le résultat détaillé : on
      // recharge la position complète (GET /position, avec `result`).
      await loadPosition()
      await loadSectionData()
    }
    setRecalc(false)
  }

  const patchAndReload = async (url, body, message) => {
    const res = await makePatchRequest(url, body, {}, true)
    if (res?.status === 200) {
      setSnack(message)
      await loadSectionData()
    }
  }

  const tagOperation = async (op, value) => {
    // '' = retour au mode auto (DELETE du tag) ; sinon POST idempotent
    // (unique user+operation côté backend → 201 créé / 200 mis à jour).
    let ok = false
    if (!value) {
      if (!op.tag_id) return
      const res = await makeDeleteRequest(
        urls.taxOperationTag(op.tag_id),
        {},
        true,
      )
      ok = res?.status === 204 || res?.status === 200
    } else {
      const res = await makePostRequest(
        urls.taxOperationTags,
        { operation: op.id, fact_type: value },
        {},
        true,
      )
      ok = res?.status === 201 || res?.status === 200
    }
    if (ok) {
      setSnack(t('taxCenter.opsSaved'))
      // Le tag marque le profil dirty côté backend : GET /position
      // (ensure_fresh) recalcule la position avec le nouveau fait.
      await loadOperations(opsQuery)
      await loadPosition()
      await loadSectionData()
    }
  }

  const answered = async () => {
    // Une réponse invalide la position : on recharge tout (la question
    // suivante, s'il y en a une, apparaît alors — une à la fois).
    await loadPosition()
    await loadSectionData()
  }

  const eventStatusTone = { UPCOMING: 'dim', IN_PROGRESS: 'warn', COMPLETED: 'good', OVERDUE: 'bad' }
  const eventStatusLabel = {
    UPCOMING: t('taxCenter.statusUpcoming'),
    IN_PROGRESS: t('taxCenter.statusInProgress'),
    COMPLETED: t('taxCenter.statusCompleted'),
    OVERDUE: t('taxCenter.statusOverdue'),
  }
  const ruleStatusLabel = {
    APPLICABLE: t('taxCenter.detStatusApplicable'),
    POTENTIALLY_APPLICABLE: t('taxCenter.detStatusPotentially'),
    NOT_APPLICABLE: t('taxCenter.detStatusNot'),
    UNKNOWN: t('taxCenter.detStatusUnknown'),
  }
  const ruleStatusTone = {
    APPLICABLE: 'good',
    POTENTIALLY_APPLICABLE: 'warn',
    NOT_APPLICABLE: 'dim',
    UNKNOWN: 'bad',
  }

  const pendingQuestion = questions[0] || null
  const details = useMemo(
    () => (position?.result?.details ? position.result.details : []),
    [position],
  )

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: P.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: P.accent }} />
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', background: P.bg, p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        {/* ------------------------------------------------------ entête */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ mb: 0.5 }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <ReceiptLongRoundedIcon sx={{ color: P.accent }} />
            <Typography sx={{ color: P.text, fontSize: 22, fontWeight: 800 }}>
              {t('taxCenter.title')}
            </Typography>
            {position?.tax_year && (
              <Chip
                size="small"
                label={`${t('taxCenter.taxYear')} ${position.tax_year}`}
                sx={{ color: P.textDim, bgcolor: 'rgba(255,255,255,0.06)' }}
              />
            )}
          </Stack>
          <Button
            size="small"
            startIcon={<RefreshRoundedIcon />}
            disabled={recalc}
            onClick={recalculate}
            sx={{ color: P.accent }}
          >
            {recalc ? t('taxCenter.recalculating') : t('taxCenter.recalculate')}
          </Button>
        </Stack>
        <Typography sx={{ color: P.textDim, fontSize: 12.5, mb: 2 }}>
          {t('taxCenter.subtitle')}
        </Typography>

        {/* --------------------------------------- couverture honnête */}
        {!position ? (
          <Alert severity="error">{t('taxCenter.loadError')}</Alert>
        ) : !position.covered ? (
          <Card accent={P.warn}>
            <Typography sx={{ color: P.text, fontSize: 15, fontWeight: 700 }}>
              {t('taxCenter.notCovered')}
            </Typography>
            <Typography sx={{ color: P.textDim, fontSize: 12.5, mt: 0.5 }}>
              {t('taxCenter.notCoveredHint')}
            </Typography>
            {/* Diagnostic : montre ce que le moteur a compris du profil,
                pour distinguer « pays mal renseigné » de « pack absent ». */}
            <Typography sx={{ color: P.warn, fontSize: 12.5, mt: 1 }}>
              {t('taxCenter.detectedCountry', {
                country: coverage?.country || '—',
              })}
            </Typography>
          </Card>
        ) : (
          <>
            {!position.reliable && (
              <Alert
                severity="warning"
                icon={<HelpOutlineRoundedIcon fontSize="small" />}
                sx={{ mb: 1.5 }}
              >
                {t('taxCenter.unreliable')}
              </Alert>
            )}

            {/* -------------------------- progressive profiling, 1 à 1 */}
            {pendingQuestion && (
              <QuestionPanel
                question={pendingQuestion}
                onAnswered={answered}
                t={t}
              />
            )}

            {/* ------------------------------------------------- KPIs */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Kpi
                label={t('taxCenter.kpiEstimatedTax')}
                value={fmtMoney(position.estimated_total_tax, currency)}
              />
              <Kpi
                label={t('taxCenter.kpiAlreadyPaid')}
                value={fmtMoney(position.already_paid, currency)}
              />
              <Kpi
                label={t('taxCenter.kpiRemaining')}
                value={fmtMoney(position.estimated_due, currency)}
                color={
                  (position.estimated_due || 0) > 0 ? P.warn : P.positive
                }
              />
              <Kpi
                label={t('taxCenter.kpiSavings')}
                value={fmtMoney(position.potential_savings, currency)}
                color={P.positive}
              />
              <Kpi
                label={t('taxCenter.kpiReadiness')}
                value={`${Math.round((position.tax_readiness || 0) * 100)} %`}
              />
              <Kpi
                label={t('taxCenter.kpiNextDeadline')}
                value={
                  position.next_deadline
                    ? fmtDate(position.next_deadline.due_date)
                    : t('taxCenter.noDeadline')
                }
                sub={position.next_deadline?.name}
              />
            </Stack>

            {/* --------------------------------------------- sections */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={section}
              onChange={(e, v) => v && setSection(v)}
              sx={{
                mb: 2,
                flexWrap: 'wrap',
                '& .MuiToggleButton-root': {
                  color: P.textDim,
                  borderColor: P.border,
                  fontSize: 12,
                  textTransform: 'none',
                  px: 1.5,
                  '&.Mui-selected': {
                    color: P.text,
                    bgcolor: 'rgba(126,156,219,0.18)',
                  },
                },
              }}
            >
              <ToggleButton value="overview">
                {t('taxCenter.sectionOverview')}
              </ToggleButton>
              <ToggleButton value="opportunities">
                {t('taxCenter.sectionOpportunities')}
              </ToggleButton>
              <ToggleButton value="calendar">
                {t('taxCenter.sectionCalendar')}
              </ToggleButton>
              <ToggleButton value="documents">
                {t('taxCenter.sectionDocuments')}
              </ToggleButton>
              <ToggleButton value="operations">
                {t('taxCenter.sectionOperations')}
              </ToggleButton>
              <ToggleButton value="simulations">
                {t('taxCenter.sectionSimulations')}
              </ToggleButton>
              <ToggleButton value="details">
                {t('taxCenter.sectionDetails')}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* ------------------------------------------------ overview */}
            {section === 'overview' && (
              <>
                <Card title={t('taxCenter.sectionOverview')}>
                  <Stack spacing={0.8}>
                    {[
                      ['grossIncome', position.gross_income],
                      ['totalDeductions', -Math.abs(position.total_deductions || 0)],
                      ['taxableIncome', position.taxable_income],
                      ['taxableWealth', position.taxable_wealth],
                    ].map(([key, val]) => (
                      <Stack
                        key={key}
                        direction="row"
                        justifyContent="space-between"
                      >
                        <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                          {t(`taxCenter.${key}`)}
                        </Typography>
                        <Typography
                          sx={{
                            color: val < 0 ? P.positive : P.text,
                            fontFamily: P.mono,
                            fontSize: 13,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {fmtMoney(val, currency)}
                        </Typography>
                      </Stack>
                    ))}
                    <Box sx={{ borderTop: `1px solid ${P.border}`, pt: 0.8 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography
                          sx={{ color: P.text, fontSize: 13, fontWeight: 700 }}
                        >
                          {t('taxCenter.kpiEstimatedTax')}
                        </Typography>
                        <Typography
                          sx={{
                            color: P.text,
                            fontFamily: P.mono,
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                        >
                          {fmtMoney(position.estimated_total_tax, currency)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap">
                    <Typography sx={{ color: P.textDim, fontSize: 11.5 }}>
                      {t('taxCenter.jurisdiction')} :{' '}
                      <b style={{ color: P.text }}>{position.jurisdiction || '—'}</b>
                    </Typography>
                    <Typography sx={{ color: P.textDim, fontSize: 11.5 }}>
                      {t('taxCenter.computedAt')} : {fmtDate(position.computed_at)}
                    </Typography>
                    <Typography sx={{ color: P.textDim, fontSize: 11.5 }}>
                      {t('taxCenter.profileCompleteness')} :{' '}
                      {Math.round((position.profile_completeness || 0) * 100)} %
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (position.profile_completeness || 0) * 100)}
                    sx={{
                      mt: 1,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: 'rgba(255,255,255,0.08)',
                      '& .MuiLinearProgress-bar': { bgcolor: P.accent },
                    }}
                  />
                </Card>
                <CopilotBox t={t} />
              </>
            )}

            {/* -------------------------------------------- opportunités */}
            {section === 'opportunities' &&
              (opportunities.length === 0 ? (
                <Card>
                  <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                    {t('taxCenter.oppEmpty')}
                  </Typography>
                </Card>
              ) : (
                opportunities.map((opp) => (
                  <Card
                    key={opp.id}
                    title={opp.title}
                    accent={
                      opp.liquidity_impact === 'BLOCKING' ? P.negative : P.positive
                    }
                    right={
                      <Stack direction="row" spacing={0.5}>
                        {opp.status === 'OPEN' && (
                          <>
                            <Button
                              size="small"
                              sx={{ color: P.positive, fontSize: 11 }}
                              onClick={() =>
                                patchAndReload(
                                  `${urls.taxOpportunities}${opp.id}/`,
                                  { status: 'ACTIONED' },
                                  t('taxCenter.oppActioned'),
                                )
                              }
                            >
                              {t('taxCenter.oppActioned')}
                            </Button>
                            <Button
                              size="small"
                              sx={{ color: P.textDim, fontSize: 11 }}
                              onClick={() =>
                                patchAndReload(
                                  `${urls.taxOpportunities}${opp.id}/`,
                                  { status: 'DISMISSED' },
                                  t('taxCenter.oppDismiss'),
                                )
                              }
                            >
                              {t('taxCenter.oppDismiss')}
                            </Button>
                          </>
                        )}
                        {opp.status !== 'OPEN' && (
                          <StatusChip label={opp.status} tone="dim" />
                        )}
                      </Stack>
                    }
                  >
                    <Typography sx={{ color: P.textDim, fontSize: 12.5, mb: 1 }}>
                      {opp.description}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        <TrendingDownRoundedIcon
                          sx={{ fontSize: 14, color: P.positive, mr: 0.4, verticalAlign: 'middle' }}
                        />
                        {t('taxCenter.oppSaving')} :{' '}
                        <b style={{ color: P.positive }}>
                          {fmtMoney(opp.estimated_saving, currency)}
                        </b>
                      </Typography>
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        {t('taxCenter.oppRecommended')} :{' '}
                        <b style={{ color: P.text }}>
                          {fmtMoney(opp.recommended_amount, currency)}
                        </b>
                      </Typography>
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        {t('taxCenter.oppRequiredCash')} :{' '}
                        {fmtMoney(opp.required_cash, currency)}
                      </Typography>
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        {t('taxCenter.oppDeadline')} : {fmtDate(opp.deadline)}
                      </Typography>
                    </Stack>
                    {opp.liquidity_impact === 'BLOCKING' && (
                      <Tooltip title={t('taxCenter.oppBlockedHint')}>
                        <Box sx={{ mt: 1 }}>
                          <StatusChip label={t('taxCenter.oppBlocked')} tone="bad" />
                        </Box>
                      </Tooltip>
                    )}
                    {opp.required_action && (
                      <Typography sx={{ color: P.text, fontSize: 12.5, mt: 1 }}>
                        → {opp.required_action}
                      </Typography>
                    )}
                  </Card>
                ))
              ))}

            {/* ---------------------------------------------- calendrier */}
            {section === 'calendar' &&
              (events.length === 0 ? (
                <Card>
                  <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                    {t('taxCenter.calEmpty')}
                  </Typography>
                </Card>
              ) : (
                events.map((ev) => (
                  <Card
                    key={ev.id}
                    title={ev.name}
                    accent={ev.status === 'OVERDUE' ? P.negative : P.accent}
                    right={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <StatusChip
                          label={eventStatusLabel[ev.status] || ev.status}
                          tone={eventStatusTone[ev.status] || 'dim'}
                        />
                        {(ev.status === 'UPCOMING' || ev.status === 'OVERDUE' ||
                          ev.status === 'IN_PROGRESS') && (
                          <Button
                            size="small"
                            sx={{ color: P.positive, fontSize: 11 }}
                            onClick={() =>
                              patchAndReload(
                                `${urls.taxCalendar}${ev.id}/`,
                                { status: 'COMPLETED' },
                                t('taxCenter.statusCompleted'),
                              )
                            }
                          >
                            {t('taxCenter.calMarkDone')}
                          </Button>
                        )}
                      </Stack>
                    }
                  >
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        <EventRoundedIcon
                          sx={{ fontSize: 14, mr: 0.4, verticalAlign: 'middle' }}
                        />
                        {t('taxCenter.calDue')} :{' '}
                        <b style={{ color: P.text }}>{fmtDate(ev.due_date)}</b>
                      </Typography>
                      {ev.estimated_amount !== null &&
                        ev.estimated_amount !== undefined && (
                          <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                            {t('taxCenter.calAmount')} :{' '}
                            {fmtMoney(ev.estimated_amount, currency)}
                          </Typography>
                        )}
                      <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                        {ev.jurisdiction}
                      </Typography>
                    </Stack>
                    {ev.required_action && (
                      <Typography sx={{ color: P.text, fontSize: 12.5, mt: 1 }}>
                        → {ev.required_action}
                      </Typography>
                    )}
                  </Card>
                ))
              ))}

            {/* -------------------------------------------- justificatifs */}
            {section === 'documents' &&
              (documents.length === 0 ? (
                <Card>
                  <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                    {t('taxCenter.docEmpty')}
                  </Typography>
                </Card>
              ) : (
                documents.map((doc) => (
                  <Card
                    key={doc.id}
                    title={doc.evidence_type}
                    accent={doc.status === 'MISSING' ? P.warn : P.positive}
                    right={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <StatusChip
                          label={
                            doc.status === 'MISSING'
                              ? t('taxCenter.docMissing')
                              : t('taxCenter.docProvided')
                          }
                          tone={doc.status === 'MISSING' ? 'warn' : 'good'}
                        />
                        {doc.status === 'MISSING' && (
                          <Button
                            size="small"
                            sx={{ color: P.positive, fontSize: 11 }}
                            onClick={() =>
                              patchAndReload(
                                `${urls.taxDocuments}${doc.id}/`,
                                { status: 'PROVIDED' },
                                t('taxCenter.docProvided'),
                              )
                            }
                          >
                            {t('taxCenter.docMarkProvided')}
                          </Button>
                        )}
                      </Stack>
                    }
                  >
                    <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
                      {doc.description}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      {doc.rule_code && (
                        <Typography sx={{ color: P.textDim, fontSize: 11.5 }}>
                          {doc.rule_code}
                        </Typography>
                      )}
                      {doc.amount !== null && doc.amount !== undefined && (
                        <Typography sx={{ color: P.textDim, fontSize: 11.5 }}>
                          {fmtMoney(doc.amount, currency)}
                        </Typography>
                      )}
                    </Stack>
                  </Card>
                ))
              ))}

            {/* ----------------------------- opérations (flag manuel) */}
            {section === 'operations' && (
              <Card
                title={t('taxCenter.sectionOperations')}
                right={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      placeholder={t('taxCenter.opsSearch')}
                      value={opsQuery}
                      onChange={(e) => setOpsQuery(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' && loadOperations(opsQuery)
                      }
                      sx={{ ...fieldSx, width: { xs: 150, sm: 220 } }}
                    />
                    <Button
                      size="small"
                      sx={{ color: P.accent }}
                      onClick={() => loadOperations(opsQuery)}
                    >
                      {t('taxCenter.opsSearchGo')}
                    </Button>
                  </Stack>
                }
              >
                <Typography sx={{ color: P.textDim, fontSize: 11.5, mb: 1.5 }}>
                  {t('taxCenter.opsHint')}
                </Typography>
                {opsLoading ? (
                  <CircularProgress size={20} sx={{ color: P.accent }} />
                ) : operations.length === 0 ? (
                  <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                    {t('taxCenter.opsEmpty')}
                  </Typography>
                ) : (
                  <Stack spacing={0.8}>
                    {operations.map((op) => (
                      <Stack
                        key={op.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          borderBottom: `1px solid ${P.border}`,
                          pb: 0.8,
                          gap: 0.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: '1 1 260px', pr: 1 }}>
                          <Typography
                            sx={{
                              color: P.text,
                              fontSize: 12.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {op.libelle || '—'}
                          </Typography>
                          <Typography sx={{ color: P.textDim, fontSize: 11 }}>
                            {fmtDate(op.date)} ·{' '}
                            <span
                              style={{
                                fontFamily: P.mono,
                                color:
                                  op.type_operation === 'D'
                                    ? P.negative
                                    : P.positive,
                              }}
                            >
                              {fmtMoney(op.montant, currency)}
                            </span>
                            {!op.tagged_fact_type && op.detected_fact_type && (
                              <>
                                {' · '}
                                {t('taxCenter.opsDetected')} :{' '}
                                {t(
                                  `taxCenter.factTypes.${op.detected_fact_type}`,
                                  op.detected_fact_type,
                                )}
                              </>
                            )}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {op.tagged_fact_type && (
                            <StatusChip
                              label={t('taxCenter.opsManual')}
                              tone={
                                op.tagged_fact_type === 'EXCLUDED'
                                  ? 'dim'
                                  : 'good'
                              }
                            />
                          )}
                          <Select
                            size="small"
                            value={op.tagged_fact_type || ''}
                            onChange={(e) => tagOperation(op, e.target.value)}
                            displayEmpty
                            sx={{ ...selectSx, minWidth: 190 }}
                            MenuProps={menuProps}
                          >
                            <MenuItem value="">
                              {t('taxCenter.opsAuto')}
                            </MenuItem>
                            {TAGGABLE_FACT_TYPES.map((ft) => (
                              <MenuItem key={ft} value={ft}>
                                {t(`taxCenter.factTypes.${ft}`, ft)}
                              </MenuItem>
                            ))}
                            <MenuItem value="EXCLUDED">
                              {t('taxCenter.opsExcluded')}
                            </MenuItem>
                          </Select>
                        </Stack>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Card>
            )}

            {/* --------------------------------------------- simulations */}
            {section === 'simulations' && (
              <>
                <SimulationForm t={t} onCreated={loadSectionData} />
                {scenarios.length === 0 ? (
                  <Card>
                    <Typography sx={{ color: P.textDim, fontSize: 13 }}>
                      {t('taxCenter.simEmpty')}
                    </Typography>
                  </Card>
                ) : (
                  scenarios.map((sc) => (
                    <Card
                      key={sc.id}
                      title={sc.name}
                      accent={
                        Number(sc.tax_impact) <= 0 ? P.positive : P.negative
                      }
                      right={<StatusChip label={sc.scenario_type} tone="dim" />}
                    >
                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                        <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                          {t('taxCenter.simBefore')} :{' '}
                          {fmtMoney(sc.result_before?.estimated_total_tax, currency)}
                        </Typography>
                        <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                          {t('taxCenter.simAfter')} :{' '}
                          {fmtMoney(sc.result_after?.estimated_total_tax, currency)}
                        </Typography>
                        <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                          {t('taxCenter.simTaxImpact')} :{' '}
                          <b
                            style={{
                              color:
                                Number(sc.tax_impact) <= 0 ? P.positive : P.negative,
                            }}
                          >
                            {fmtMoney(sc.tax_impact, currency)}
                          </b>
                        </Typography>
                        {sc.cashflow_impact !== null &&
                          sc.cashflow_impact !== undefined && (
                            <Typography sx={{ color: P.textDim, fontSize: 12 }}>
                              {t('taxCenter.simCashflowImpact')} :{' '}
                              {fmtMoney(sc.cashflow_impact, currency)}
                            </Typography>
                          )}
                      </Stack>
                      {sc.recommendation && (
                        <Typography sx={{ color: P.text, fontSize: 12.5, mt: 1 }}>
                          → {sc.recommendation}
                        </Typography>
                      )}
                    </Card>
                  ))
                )}
              </>
            )}

            {/* ------------------------------------------------- détails */}
            {section === 'details' && (
              <>
                <Card title={t('taxCenter.breakdown')}>
                  {details.length === 0 ? (
                    <Typography sx={{ color: P.textDim, fontSize: 13 }}>—</Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      {details.map((d) => (
                        <Stack
                          key={`${d.code}-${d.output ?? d.kind ?? ''}`}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
                            {d.name || d.code}
                          </Typography>
                          <Typography
                            sx={{
                              color: P.text,
                              fontFamily: P.mono,
                              fontSize: 12.5,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {fmtMoney(d.amount, currency)}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Card>
                <Card title={t('taxCenter.detRules')}>
                  <Stack spacing={0.8}>
                    {rules.map((r) => (
                      <Stack
                        key={r.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexWrap="wrap"
                      >
                        <Box sx={{ minWidth: 0, pr: 1 }}>
                          <Typography sx={{ color: P.text, fontSize: 12.5 }}>
                            {r.name}
                          </Typography>
                          <Typography sx={{ color: P.textDim, fontSize: 11 }}>
                            {r.code} · {r.jurisdiction} · {r.validation_status}
                            {(r.missing_facts || []).length > 0 &&
                              ` · ${t('taxCenter.detMissingFacts')} : ${r.missing_facts.join(', ')}`}
                          </Typography>
                        </Box>
                        <StatusChip
                          label={ruleStatusLabel[r.status] || r.status}
                          tone={ruleStatusTone[r.status] || 'dim'}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Card>
                {coverage && (
                  <Card title={t('taxCenter.detCoverage')}>
                    <Stack spacing={0.6}>
                      {(coverage.areas || []).map((a) => (
                        <Stack
                          key={`${a.jurisdiction}-${a.area}`}
                          direction="row"
                          justifyContent="space-between"
                        >
                          <Typography sx={{ color: P.textDim, fontSize: 12.5 }}>
                            {a.jurisdiction} · {a.area}
                          </Typography>
                          <StatusChip
                            label={a.level}
                            tone={
                              a.level === 'FULL'
                                ? 'good'
                                : a.level === 'UNSUPPORTED'
                                  ? 'bad'
                                  : 'warn'
                            }
                          />
                        </Stack>
                      ))}
                    </Stack>
                  </Card>
                )}
                {position.result?.sources?.length > 0 && (
                  <Card title={t('taxCenter.detSources')}>
                    <Stack spacing={0.4}>
                      {position.result.sources.map((s) => (
                        <Typography
                          key={s.url || s.title}
                          component="a"
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          sx={{
                            color: P.accent,
                            fontSize: 12,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {s.title || s.authority || s.url}
                        </Typography>
                      ))}
                    </Stack>
                  </Card>
                )}
              </>
            )}
          </>
        )}
      </Box>
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={2500}
        onClose={() => setSnack('')}
        message={snack}
      />
    </Box>
  )
}

export default TaxCenterPage
